import { Paper, Stack } from '@mui/material';
import { DataGridPro, GridRowId, GridRowModel, GridRowModes, GridRowModesModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@parkchirp/api-client';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { CreditApplicationType, InvoiceAdjustmentTypeEnum, LineItemEntryTypeEnum } from 'parkchirp-shared/enums';
import React, { useEffect, useState } from 'react';

import { GET_INVOICE_LINE_ITEM_ENTRIES } from '../../../queries';
import { datagridFilterPanelAttributes } from '../../../utils';
import { DatagridLinearProgress } from '../../loading';
import { DataGridAddBatchToolbar } from './batchToolbar';
import { getInvoiceLineItemEntryColumns, lineItemIsNegative, sumAmounts } from './invoiceLineItemEntryColumns';
import useAdjustInvoiceContext from './useAdjustInvoiceContext';

interface Amounts {
	amount: number;
	amountTax1: number;
	amountTax2: number;
	amountTax3: number;
	amountTax4: number;
	gross: number;
}

export const InvoiceLineItemDetail = ({ row, timezone, setShowSnackbar }) => {
	const {
		isAdjustingEachLineItem,
		lineItemAdjustmentMap,
		setLineItemAdjustmentMap,
		getAllLineItemAdjustmentsFromMap,
	} = useAdjustInvoiceContext();
	const adjustments = getAllLineItemAdjustmentsFromMap();

	function getCorrectLineItemEntryType(lineItemEntryType, creditApplicationType) {
		const lineItemEntryTypeAsEnum = LineItemEntryTypeEnum[lineItemEntryType];
		const creditApplicationTypeAsEnum = CreditApplicationType[creditApplicationType];

		if (lineItemEntryTypeAsEnum === LineItemEntryTypeEnum.REFUND) {
			return 'Refund - Payment';
		}

		if (lineItemEntryTypeAsEnum === LineItemEntryTypeEnum.PAYMENT) {
			if (!creditApplicationTypeAsEnum) {
				return lineItemEntryTypeAsEnum;
			}
			const maybeFormattedPaymentType = {
				[CreditApplicationType.CARRYOVER_CREDIT]: 'Payment - Carryover Credit Applied',
				[CreditApplicationType.APPLY_CASH]: 'Payment - Credit No Cash Applied',
				[CreditApplicationType.DISCOUNT_CREDIT]: 'Payment - Discount Credit Applied',
			}[creditApplicationTypeAsEnum];
			return maybeFormattedPaymentType || 'Payment - Credit Cash Applied';
		}
		if (lineItemEntryType === LineItemEntryTypeEnum.UNAPPLY_CREDIT) {
			const maybeFormattedUnapplyType = {
				[CreditApplicationType.CARRYOVER_CREDIT]: 'Unapply - Carryover Credit',
				[CreditApplicationType.APPLY_CASH]: 'Unapply - Credit No Cash Applied',
				[CreditApplicationType.DISCOUNT_CREDIT]: 'Unapply - Discount Credit',
			}[creditApplicationTypeAsEnum];
			return maybeFormattedUnapplyType || 'Unapply - Credit Cash Applied';
		}
		if (lineItemEntryTypeAsEnum === LineItemEntryTypeEnum.REVERSAL) {
			if (creditApplicationType) {
				return 'Reversal - Credit Payment';
			}
			return 'Reversal - Payment';
		}
		return lineItemEntryTypeAsEnum;
	}

	function getCorrectApplicationDate(lineItemEntry) {
		if (lineItemEntry.applicationDate) {
			return dayjs(lineItemEntry.applicationDate).tz('UTC').format('M/D/YYYY');
		}
		return null;
	}

	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const [rows, setRows] = useState([]);
	const [totals, setTotals] = useState<Amounts>({
		amount: 0,
		amountTax1: 0,
		amountTax2: 0,
		amountTax3: 0,
		amountTax4: 0,
		gross: 0,
	});

	function calculateTotalsFromRows(rowsToUse) {
		let totalAmount = 0;
		let totalAmountTax1 = 0;
		let totalAmountTax2 = 0;
		let totalAmountTax3 = 0;
		let totalAmountTax4 = 0;
		let totalGross = 0;
		rowsToUse.forEach((data) => {
			let { amount, amountTax1, amountTax2, amountTax3, amountTax4 } = data;
			let gross = sumAmounts(data);
			if (lineItemIsNegative(LineItemEntryTypeEnum[data.type])) {
				amount *= -1;
				amountTax1 *= -1;
				amountTax2 *= -1;
				amountTax3 *= -1;
				amountTax4 *= -1;
				gross *= -1;
			}
			totalAmount += amount;
			totalAmountTax1 += amountTax1;
			totalAmountTax2 += amountTax2;
			totalAmountTax3 += amountTax3;
			totalAmountTax4 += amountTax4;
			totalGross += gross;
		});
		setTotals({
			amount: totalAmount,
			amountTax1: totalAmountTax1,
			amountTax2: totalAmountTax2,
			amountTax3: totalAmountTax3,
			amountTax4: totalAmountTax4,
			gross: totalGross,
		});
	}

	const { loading } = useQuery(GET_INVOICE_LINE_ITEM_ENTRIES, {
		context: {
			apiVersion: 'v2',
		},
		variables: {
			lineItemId: row.lineItemId,
		},
		fetchPolicy: 'no-cache',
		onCompleted: (response) => {
			if (response?.getInvoiceLineItemEntries) {
				const rowsToUse = response?.getInvoiceLineItemEntries.map((data, index) => ({
					id: index,
					createdAt: dayjs(data.createdAt).tz(timezone).format('M/D/YYYY h:mm A'),
					applicationDate: getCorrectApplicationDate(data),
					formattedType: getCorrectLineItemEntryType(data.type, data.creditApplicationType),
					type: data.type,
					amount: data.amount,
					amountTax1: data.amountTax1,
					amountTax2: data.amountTax2,
					amountTax3: data.amountTax3,
					amountTax4: data.amountTax4,
					gross: sumAmounts(data),
				}));
				calculateTotalsFromRows(response?.getInvoiceLineItemEntries);
				const adjustmentsForThisLineItem = adjustments.filter((a) => a.lineItemId === row.lineItemId);
				setRows([...rowsToUse, ...adjustmentsForThisLineItem]);
			}
		},
	});

	function getTotalRow() {
		if (loading) {
			return { id: 1 };
		}
		return {
			id: rows.length,
			type: 'Total',
			...totals,
		};
	}

	// when the save button is clicked on a datagrid row, the row
	// is by default no longer editable. in the case of an error
	// related to saving the row occurs, we need to revert the
	// row's state to keep it editable by the user.
	const keepRowEditable = (id: GridRowId) => {
		setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
	};

	const processRowUpdate = async (newRow: GridRowModel) => {
		if (!newRow.type) {
			setShowSnackbar({
				message: `Unable to save Adjustment: type is required.`,
				severity: 'error',
			});
			keepRowEditable(newRow.id);
			return newRow;
		}
		if (sumAmounts(newRow) === 0) {
			setShowSnackbar({
				message: `Adjustment total must be non zero.`,
				severity: 'error',
			});
			keepRowEditable(newRow.id);
			return newRow;
		}
		const nonNegativeFields = [
			{ label: 'Net Amount', key: 'amount', value: round(newRow.amount * 100, 2) },
			{ label: 'Tax 1', key: 'amountTax1', value: round(newRow.amountTax1 * 100, 2) },
			{ label: 'Tax 2', key: 'amountTax2', value: round(newRow.amountTax2 * 100, 2) },
			{ label: 'Tax 3', key: 'amountTax3', value: round(newRow.amountTax3 * 100, 2) },
			{ label: 'Tax 4', key: 'amountTax4', value: round(newRow.amountTax4 * 100, 2) },
		];
		const missingFields = nonNegativeFields.filter(({ value }) => value < 0);
		const fieldsTooLarge = nonNegativeFields.filter(
			({ value, key }) =>
				value > totals[key] &&
				![
					InvoiceAdjustmentTypeEnum.ADJUSTMENT_POSITIVE,
					InvoiceAdjustmentTypeEnum.TERMINATION_ADJUSTMENT_POSITIVE,
				].includes(InvoiceAdjustmentTypeEnum[newRow.type]),
		);

		if (missingFields.length) {
			const missingAsString = missingFields.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to save Adjustment: ${missingAsString} cannot be negative.`,
				severity: 'error',
			});
			keepRowEditable(newRow.id);
			return newRow;
		}

		if (fieldsTooLarge.length) {
			const missingAsString = fieldsTooLarge.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to save Adjustment: ${missingAsString} exceed(s) the line item's value.`,
				severity: 'error',
			});
			keepRowEditable(newRow.id);
			return newRow;
		}

		const copyOfAdjustmentMap = new Map(lineItemAdjustmentMap);
		const rowToAdd = {
			id: newRow.id,
			type: newRow.type,
			adjustmentType: newRow.type,
			amount: round(newRow.amount * 100, 2),
			amountTax1: round(newRow.amountTax1 * 100, 2),
			amountTax2: round(newRow.amountTax2 * 100, 2),
			amountTax3: round(newRow.amountTax3 * 100, 2),
			amountTax4: round(newRow.amountTax4 * 100, 2),
			gross: round(sumAmounts(newRow) * 100, 2),
			lineItemId: row.lineItemId,
			isNew: newRow.isNew,
		};
		if (lineItemAdjustmentMap.has(row.lineItemId)) {
			const currentEntryAdjustments = copyOfAdjustmentMap.get(row.lineItemId);
			if (currentEntryAdjustments) {
				const adjustmentAlreadyExists = currentEntryAdjustments.some(({ id }) => id === newRow.id);
				let updatedEntryAdjustments = [];
				if (adjustmentAlreadyExists) {
					updatedEntryAdjustments = currentEntryAdjustments.map((entry) => {
						if (entry.id === newRow.id) {
							return rowToAdd;
						}
						return entry;
					});
				} else {
					updatedEntryAdjustments = [...currentEntryAdjustments, rowToAdd];
				}
				copyOfAdjustmentMap.set(row.lineItemId, updatedEntryAdjustments);
			}
		} else {
			copyOfAdjustmentMap.set(row.lineItemId, [
				{
					...rowToAdd,
				},
			]);
		}
		setLineItemAdjustmentMap(copyOfAdjustmentMap);
		const rowsUpdated = rows.map((entry) => (entry.id === newRow.id ? rowToAdd : entry));
		setRows(rowsUpdated);
		return { ...newRow };
	};

	useEffect(() => {
		// remove any unsaved adjustments from the datagrid if we are no longer
		// adjusting by line item
		if (!isAdjustingEachLineItem) {
			const rowsUpdated = rows.filter(({ isNew }) => !isNew);
			// only update rows if there is a mismatch, this avoids a
			// "maximum update depth exceeded" error
			if (rowsUpdated.length !== rows.length) {
				setRows(rowsUpdated);
			}
		}
	}, [isAdjustingEachLineItem, rows]);

	const deleteHandler = (id: GridRowId) => {
		const copyOfAdjustmentMap = new Map(lineItemAdjustmentMap);
		const updatedAdjustmentList = lineItemAdjustmentMap
			.get(row.lineItemId)
			.filter((adjustment) => adjustment.id !== id);
		copyOfAdjustmentMap.set(row.lineItemId, updatedAdjustmentList);
		setLineItemAdjustmentMap(copyOfAdjustmentMap);
	};

	useEffect(() => {
		// only calculate the total from rows that arent currently being edited
		calculateTotalsFromRows(rows.filter(({ id }) => rowModesModel[id]?.mode !== GridRowModes.Edit));
	}, [rows, rowModesModel]);

	return (
		<Stack
			sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
			direction='column'
			data-cy='invoice-line-item-detail-table-container'
		>
			<Paper sx={{ flex: 1, mx: 'auto', width: '90%', p: 1 }}>
				<Stack direction='column' spacing={1} sx={{ height: 1 }}>
					<DataGridPro
						loading={loading}
						initialState={{ density: 'compact' }}
						editMode='row'
						columns={getInvoiceLineItemEntryColumns({
							isAdjustingEachLineItem,
							rowModesModel,
							setRowModesModel,
							rows,
							setRows,
							deleteHandler,
						})}
						slots={{
							loadingOverlay: DatagridLinearProgress,
							toolbar: (args) =>
								DataGridAddBatchToolbar({
									...args,
									loading,
									setRows,
									setRowModesModel,
								}),
						}}
						slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
						// the total row will not have the createdAt field
						// nor will it have isNew set to true, so we give
						// it a custom class
						getRowClassName={(params) => (params.row.createdAt || params.row.isNew ? '' : 'totalRow')}
						rows={[...rows, getTotalRow()]}
						processRowUpdate={processRowUpdate}
						rowModesModel={rowModesModel}
						onProcessRowUpdateError={console.error}
						sx={{ flex: 1, p: 3, minHeight: 140 }}
					/>
				</Stack>
			</Paper>
		</Stack>
	);
};
